var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Tab, Tabs } from "@mui/material";
import { SentMessages } from "./SentMessages";
import { useFakeGetStarted, useSentMessages } from "./useSentMessages";
import { SENT_MESSAGES_PARAM, SENT_MESSAGES_REFRESH_MS, modeParam, parseMode, } from "./types";
/**
 * Settings > Messaging: Accounts, and Sent messages where the dev fake
 * provider routes are mounted. The Sent messages tab exists only once
 * `messaging_fake_provider_messages` answers; where it fails (the routes
 * aren't mounted, or the user can't write accounts) there are no tabs, just
 * the accounts. There's no other switch.
 *
 * Its own query parameter, `?channel=`, keeps the mode and selects the tab:
 * the page's shared Tabs reads `tabIndex` from the URL.
 */
export var MessagingTabs = function (_a) {
    var accounts = _a.accounts, _b = _a.refreshMs, refreshMs = _b === void 0 ? SENT_MESSAGES_REFRESH_MS : _b;
    var t = useTranslation().t;
    var _c = useSearchParams(), searchParams = _c[0], setSearchParams = _c[1];
    var param = searchParams.get(SENT_MESSAGES_PARAM);
    var mode = parseMode(param);
    // Read once to decide whether the tab exists; refreshed while it's open.
    var sent = useSentMessages({ active: param !== null, refreshMs: refreshMs });
    var getStarted = useFakeGetStarted();
    var tab = param !== null && sent.available ? "SENT_MESSAGES" : "ACCOUNTS";
    var setParam = useCallback(function (value) {
        return setSearchParams(function (current) {
            var next = new URLSearchParams(current);
            if (value === null) {
                next.delete(SENT_MESSAGES_PARAM);
            }
            else {
                next.set(SENT_MESSAGES_PARAM, value);
            }
            return next;
        }, { replace: true });
    }, [setSearchParams]);
    var refresh = sent.refresh;
    var onGetStarted = useCallback(function (link) { return __awaiter(void 0, void 0, void 0, function () {
        var answer;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getStarted(link)];
                case 1:
                    answer = _a.sent();
                    refresh();
                    return [2 /*return*/, answer];
            }
        });
    }); }, [getStarted, refresh]);
    // The same tree with or without the tabs, so the account list never remounts.
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column" }, children: [sent.available ? (_jsxs(Tabs, { value: tab, onChange: function (_event, value) {
                    return setParam(value === "SENT_MESSAGES" ? modeParam(mode) : null);
                }, "aria-label": t("sentMessages.tabsLabel"), variant: "scrollable", allowScrollButtonsMobile: true, sx: { px: 2, borderBottom: 1, borderColor: "divider" }, children: [_jsx(Tab, { value: "ACCOUNTS", label: t("sentMessages.accountsTab") }), _jsx(Tab, { value: "SENT_MESSAGES", label: t("sentMessages.tab") })] })) : null, tab === "SENT_MESSAGES" ? (_jsx(Box, { sx: { p: 2 }, children: _jsx(SentMessages, { messages: sent.messages, mode: mode, onModeChange: function (next) { return setParam(modeParam(next)); }, onRefresh: sent.refresh, refreshing: sent.refreshing, refreshFailed: !!sent.error, onGetStarted: onGetStarted }) })) : (accounts)] }));
};
